/**
 * Single source of truth for hall-pass / destination colors.
 *
 * Every hall-pass surface (Pass Types, Pass Log, Pass Limits, Who Was Out, the
 * dashboard) should read colors from here so a pass type looks the same
 * everywhere. Until the Pass Types config is backed by the API, the
 * name → color mapping below mirrors the Pass Types defaults.
 */

// Identity colors deliberately exclude every "meaning" hue: red (overdue /
// alert), green (active / go), amber + orange (waiting / caution), and purple
// (the Hall Pass feature accent). Pass types always render WITH their name,
// so identity color is reinforcement, not the message — state colors are the
// ones that must stand alone.
export type PassColorKey = 'blue' | 'teal' | 'cyan' | 'sky' | 'indigo' | 'pink' | 'fuchsia' | 'gray';

/**
 * Tailwind background classes per color. Listed literally so JIT keeps them.
 * `pill` is a solid fill in a darker step of the hue with white text (each
 * shade clears ~4.5:1) — the active-pass pill is the one identity surface
 * that stays full-saturation so it pops on a dimmed student card.
 */
export const PASS_COLORS: Record<
  PassColorKey,
  { dot: string; bar: string; pill: string; text: string }
> = {
  blue: { dot: 'bg-primary-500', bar: 'bg-primary-500', pill: 'bg-primary-600 text-white', text: 'text-primary-600' },
  teal: { dot: 'bg-teal-500', bar: 'bg-teal-500', pill: 'bg-teal-700 text-white', text: 'text-teal-600' },
  cyan: { dot: 'bg-cyan-500', bar: 'bg-cyan-500', pill: 'bg-cyan-700 text-white', text: 'text-cyan-600' },
  sky: { dot: 'bg-sky-500', bar: 'bg-sky-500', pill: 'bg-sky-700 text-white', text: 'text-sky-600' },
  indigo: { dot: 'bg-indigo-500', bar: 'bg-indigo-500', pill: 'bg-indigo-600 text-white', text: 'text-indigo-600' },
  pink: { dot: 'bg-pink-500', bar: 'bg-pink-500', pill: 'bg-pink-600 text-white', text: 'text-pink-600' },
  fuchsia: { dot: 'bg-fuchsia-500', bar: 'bg-fuchsia-500', pill: 'bg-fuchsia-600 text-white', text: 'text-fuchsia-600' },
  gray: { dot: 'bg-gray-500', bar: 'bg-gray-500', pill: 'bg-gray-600 text-white', text: 'text-gray-600' },
};

export const PASS_COLOR_KEYS = Object.keys(PASS_COLORS) as PassColorKey[];

/** Dot background class for an explicit color key (used by the Pass Types editor). */
export const colorDot = (key: PassColorKey) => PASS_COLORS[key].dot;

/**
 * Canonical destination/pass-type → color key, by NAME.
 *
 * A fallback only. The real color is the one a school picked in Pass Types
 * (`metadata.color`); resolve through {@link makePassColors} wherever the
 * destination catalog is in hand. This table is what a destination gets when
 * it has no stored color — including every name not listed here, which lands
 * on gray. Names are normalized so the log's "1st Floor Bathroom" /
 * "2nd Floor Bathroom" match the "Bathroom" pass type.
 */
const NAME_TO_KEY: Record<string, PassColorKey> = {
  bathroom: 'blue',
  nurse: 'pink',
  library: 'teal',
  counselor: 'indigo',
  'main office': 'cyan',
  office: 'cyan',
  gym: 'sky',
  'media lab': 'fuchsia',
};

function normalize(name: string): string {
  const n = name.trim().toLowerCase();
  if (n.includes('bathroom') || n.includes('restroom')) return 'bathroom';
  return n;
}

export function passColorKey(name: string): PassColorKey {
  return NAME_TO_KEY[normalize(name)] ?? 'gray';
}

export function passColor(name: string) {
  return PASS_COLORS[passColorKey(name)];
}

/** Tailwind dot background class for a destination/pass-type name. */
export const passDot = (name: string) => passColor(name).dot;

/** Tailwind bar background class for a destination/pass-type name. */
export const passBar = (name: string) => passColor(name).bar;

/** Tailwind solid-pill background class (white-text safe) for a destination/pass-type name. */
export const passPill = (name: string) => passColor(name).pill;

/** Tailwind text color class for a destination/pass-type name (icons, accents). */
export const passText = (name: string) => passColor(name).text;

/** A destination as the color resolver reads it — name plus its loose metadata. */
interface ColoredDestination {
  name: string;
  metadata?: { color?: unknown } | null;
}

/**
 * Color resolution bound to a destination catalog. Presentational components
 * accept this rather than importing `passDot`, so the school's picked color
 * reaches them instead of the name fallback.
 */
export interface PassColors {
  /** Resolved palette key for a destination name. */
  key: (name: string) => PassColorKey;
  /** Tailwind dot background class. */
  dot: (name: string) => string;
  /** Tailwind bar background class. */
  bar: (name: string) => string;
  /** Tailwind solid-pill background class (white-text safe). */
  pill: (name: string) => string;
  /** Tailwind text color class. */
  text: (name: string) => string;
}

/**
 * Build a resolver over the live destination catalog.
 *
 * `metadata.color` used to be write-only: Pass Types saved and rendered it,
 * and every other surface called `passDot(name)` — so a school could pick a
 * color and see it nowhere else, while any destination outside NAME_TO_KEY
 * stayed gray forever.
 *
 * Falls back to the name table for a destination with no stored color, so a
 * catalog that predates the color picker looks exactly as it does today.
 *
 * Build once per render with `useMemo(() => makePassColors(destinations),
 * [destinations])` — callers already hold the catalog via
 * `useHallPassDestinations`.
 */
export function makePassColors(
  destinations: readonly ColoredDestination[] | undefined,
): PassColors {
  const keyByName = new Map<string, PassColorKey>();
  for (const d of destinations ?? []) {
    const stored = PASS_COLOR_KEYS.find((k) => k === d?.metadata?.color);
    // Exact name: a destination's OWN stored color must not leak onto a
    // differently-named sibling that `normalize` happens to collapse with it.
    if (stored && d.name) keyByName.set(d.name, stored);
  }
  const key = (name: string): PassColorKey => keyByName.get(name) ?? passColorKey(name);
  return {
    key,
    dot: (name) => PASS_COLORS[key(name)].dot,
    bar: (name) => PASS_COLORS[key(name)].bar,
    pill: (name) => PASS_COLORS[key(name)].pill,
    text: (name) => PASS_COLORS[key(name)].text,
  };
}
